/**
 * react-i18next initialisation for agentwiki web app.
 *
 * Config decisions:
 *   - fallbackLng: 'en' — always falls back to English, never shows raw keys
 *   - returnEmptyString: false — treat '' values as missing, use fallback instead
 *   - detection order: localStorage → navigator → htmlTag → fallback
 *   - Missing key logger: console.warn in dev; stub for now — Analytics Engine
 *     wiring is deferred to a later phase (P7 observability).
 *     TODO(P7): replace console.warn with Analytics Engine event on missing keys.
 *
 * Locale files are imported statically so Vite tree-shakes unused locales.
 * If locale count grows, switch to i18next-http-backend with lazy loading.
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import enIngest from '../locales/en/ingest.json'
import viIngest from '../locales/vi/ingest.json'
import enLanding from '../locales/en/landing.json'
import viLanding from '../locales/vi/landing.json'
import enPackages from '../locales/en/packages.json'
import viPackages from '../locales/vi/packages.json'
// GH-251: home dashboard namespace
import enDashboard from '../locales/en/dashboard.json'
import viDashboard from '../locales/vi/dashboard.json'
// GH-258: graph provenance namespace
import enGraph from '../locales/en/graph.json'
import viGraph from '../locales/vi/graph.json'
// GH-256: artifact drawer namespace
import enArtifact from '../locales/en/artifact.json'
import viArtifact from '../locales/vi/artifact.json'
// GH-268: onboarding tour namespace (tour.* keys)
import enOnboarding from '../locales/en/onboarding.json'
import viOnboarding from '../locales/vi/onboarding.json'

const resources = {
  en: { ingest: enIngest, landing: enLanding, packages: enPackages, dashboard: enDashboard, graph: enGraph, artifact: enArtifact, onboarding: enOnboarding },
  vi: { ingest: viIngest, landing: viLanding, packages: viPackages, dashboard: viDashboard, graph: viGraph, artifact: viArtifact, onboarding: viOnboarding },
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'vi'],
    defaultNS: 'ingest',
    // GH-268: include `onboarding` so components calling useTranslation() (default ns)
    // can resolve `tour.*` keys via fallbackNS without a namespace argument.
    fallbackNS: ['onboarding'],
    ns: ['ingest', 'landing', 'packages', 'dashboard', 'graph', 'artifact', 'onboarding'],

    // Never return empty string — fall through to fallbackLng instead
    returnEmptyString: false,

    // Missing key logger (stub — Analytics Engine wiring deferred to P7)
    // TODO(P7): send missing key events to Cloudflare Analytics Engine
    saveMissing: true,
    missingKeyHandler: (lngs: readonly string[], ns: string, key: string) => {
      if (import.meta.env.DEV) {
        console.warn(`[i18n] Missing key: ${ns}:${key} (lngs: ${lngs.join(', ')})`)
      }
    },

    interpolation: {
      // React already escapes values — disable i18next escaping to avoid double-escape
      escapeValue: false,
    },

    detection: {
      // Detection order: user preference in localStorage → browser language → html lang attr
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'agentwiki_locale',
      caches: ['localStorage'],
    },

    // Disable suspense mode — we handle loading states explicitly in components
    react: {
      useSuspense: false,
    },
  })

export default i18n
